import { useRef, useEffect } from 'react';
import { 
    PhoneCall, 
    Phone, 
    User, 
    Users,
    AlertCircle,
    CheckCircle2,
    Ban
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { useEnhancedToast } from "@/components/ui/enhanced-toast";
import { PhoneInput } from '@/components/ui/phone-input';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Form, FormControl, FormField, FormItem, FormMessage, FormLabel } from '@/components/ui/form';
import type { Assistant } from "@/api/assistants/assistants.types";
import { createCall } from '@/api/calls/calls.api';
import type { CreateCallRequest } from '@/api/calls/calls.types';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { Combobox } from "@/components/ui/combobox";
import { useAuth } from '@/context/AuthContext';
import type { MultiAgent } from '@/api/multi-agent/multi_agent.types';

interface OutboundCallTabProps {
    formRef?: React.RefObject<HTMLFormElement>;
    assistants: Assistant[];
    phoneNumberId: string;
    multiAgents: MultiAgent[];
    onFormValidityChange?: (isValid: boolean) => void;
}

const outboundFormSchema = z.object({
    phoneNumber: z
        .string()
        .min(1, 'Phone number is required')
        .refine((val) => !val || (val.startsWith('+') && isValidPhoneNumber(val)), {
            message: 'Please enter a valid phone number with country code (e.g., +49...)'
        }),
    assistant: z.string().min(1, 'Assistant is required'),
    multiAgent: z.string().optional()
});

type OutboundFormValues = z.infer<typeof outboundFormSchema>;

export function OutboundCallTab({ formRef, assistants, phoneNumberId, multiAgents, onFormValidityChange }: OutboundCallTabProps) {
    const { error, success } = useEnhancedToast();
    const { user } = useAuth();
    const canInitiateCall = user?.permissions?.phone_numbers === 'EDITOR';

    // Create refs for toast functions
    const errorRef = useRef(error);
    const successRef = useRef(success);

    // Update refs when toast functions change
    useEffect(() => {
        errorRef.current = error;
        successRef.current = success;
    }, [error, success]);

    const form = useForm<OutboundFormValues>({
        resolver: zodResolver(outboundFormSchema),
        mode: 'onChange', // Enable real-time validation
        defaultValues: {
            phoneNumber: '',
            assistant: '',
            multiAgent: '',
        },
    });

    // Watch form validity and notify parent
    useEffect(() => {
        const subscription = form.watch((values) => {
            // Form is valid if:
            // 1. Phone number is present and valid
            // 2. Either assistant OR multi-agent is selected
            const hasValidPhone = values.phoneNumber && isValidPhoneNumber(values.phoneNumber);
            const hasAssistantOrMultiAgent = !!(values.assistant || values.multiAgent);
            const isValid = !!(hasValidPhone && hasAssistantOrMultiAgent);
            
            onFormValidityChange?.(isValid);
        });
        
        return () => subscription.unsubscribe();
    }, [form, onFormValidityChange]);

    const handleInitiateCall = async (values: OutboundFormValues) => {
        if (!canInitiateCall) {
            errorRef.current({
                icon: Ban,
                title: "Permission Denied",
                description: "You need editor permissions to initiate outbound calls",
                duration: 4000,
            });
            return;
        }

        try {
            const createCallPayload: CreateCallRequest = {
                phoneNumberId,
                assistantId: values.assistant,
                customer: {
                    number: values.phoneNumber,
                },
                ...(values.multiAgent && { squadId: values.multiAgent })
            };

            const response = await createCall(createCallPayload);
            if (response.error) {
                throw new Error(response.error);
            }

            successRef.current({
                icon: CheckCircle2,
                title: "Call Initiated",
                description: "Your call has been successfully added to the queue",
                duration: 4000,
            });

        } catch (err) {
            errorRef.current({
                icon: AlertCircle,
                title: "Call Failed",
                description: err instanceof Error ? err.message : "Could not initiate the call",
                duration: 4000,
            });
        }
    };

    return (
        <Card className="shadow-sm">
            <CardHeader className="border-b bg-muted/20">
                <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                        <PhoneCall className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 space-y-1">
                        <CardTitle>Outbound Call</CardTitle>
                        <CardDescription>Initiate an outbound call to a customer</CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-6">
                <Form {...form}>
                    <form ref={formRef} onSubmit={form.handleSubmit(handleInitiateCall)} className="space-y-6">
                        {/* Outbound Phone Number */}
                        <FormField
                            control={form.control}
                            name="phoneNumber"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="flex items-center gap-2">
                                        <Phone className="h-4 w-4 text-primary" />
                                        Customer Phone Number
                                    </FormLabel>
                                    <FormControl>
                                        <PhoneInput
                                            value={field.value}
                                            onChange={field.onChange}
                                            defaultCountry="DE"
                                            international
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Assistant Selection */}
                        <FormField
                            control={form.control}
                            name="assistant"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="flex items-center gap-2">
                                        <User className="h-4 w-4 text-primary" />
                                        Assistant
                                    </FormLabel>
                                    <FormControl>
                                        <Combobox
                                            options={assistants.map(a => ({
                                                value: a.id,
                                                label: a.name,
                                                description: a.id
                                            }))}
                                            value={field.value}
                                            onValueChange={field.onChange}
                                            placeholder="Select an assistant..."
                                            searchPlaceholder="Search assistant..."
                                            emptyMessage="No assistant found."
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Multi Agent Selection */}
                        <FormField
                            control={form.control}
                            name="multiAgent"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="flex items-center gap-2">
                                        <Users className="h-4 w-4 text-primary" />
                                        Multi Agent
                                    </FormLabel>
                                    <FormControl>
                                        <Combobox
                                            options={multiAgents.map(ma => ({
                                                value: ma.id,
                                                label: ma.name,
                                                description: ma.id
                                            }))}
                                            value={field.value ?? ""}
                                            onValueChange={field.onChange}
                                            placeholder="Select a multi agent..."
                                            searchPlaceholder="Search multi agent..."
                                            emptyMessage="No multi agent found."
                                            allowClear={true}
                                        />
                                    </FormControl>
                                    <p className="text-xs text-muted-foreground">
                                        Fallback routing when assistant is unavailable
                                    </p>
                                    <FormMessage                                 />
                            </FormItem>
                        )}
                    />
                </form>
            </Form>
        </CardContent>
        </Card>
    );
}
